import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/Social.css';

const API = process.env.REACT_APP_SERVER_URL || '';
const headers = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const EMOTES = [
  { name: 'wave', icon: '👋', label: 'Wave' },
  { name: 'bow', icon: '🙇', label: 'Bow' },
  { name: 'cheer', icon: '🎉', label: 'Cheer' },
  { name: 'laugh', icon: '😄', label: 'Laugh' },
  { name: 'salute', icon: '🫡', label: 'Salute' },
  { name: 'dance', icon: '💃', label: 'Dance' },
  { name: 'shrug', icon: '🤷', label: 'Shrug' },
  { name: 'point', icon: '👉', label: 'Point' },
  { name: 'flex', icon: '💪', label: 'Flex' },
  { name: 'think', icon: '🤔', label: 'Think' },
  { name: 'cry', icon: '😢', label: 'Cry' },
  { name: 'clap', icon: '👏', label: 'Clap' },
];

const FellowshipPanel = ({
  fellowship,
  character,
  onlinePlayers = [],
  socket,
  onInspect,
  onClose,
}) => {
  const [showEmotes, setShowEmotes] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const searchTimer = useRef(null);
  const [friends, setFriends] = useState([]);

  // Fetch friend list once so we can mark friends in the nearby list
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/social/friends`, { headers: headers() });
        const data = await res.json();
        if (res.ok) setFriends((data.friends || []).map(f => f.id));
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const isLeader = fellowship?.leaderId === character?.id;
  const inFellowship = !!fellowship;

  const handleCreate = () => { if (socket) socket.emit('fellowship_create'); };
  
  const handleInvite = (targetId) => { if (socket) socket.emit('fellowship_invite', { targetId }); };
  
  const handleLeave = () => {
    if (socket && window.confirm('Leave this fellowship?')) {
      socket.emit('fellowship_leave');
    }
  };

  const handleTransfer = (targetId) => {
    if (socket && window.confirm('Transfer fellowship leadership?')) {
      socket.emit('fellowship_transfer', { targetId });
    }
  };

  const handleEmote = (emoteName) => {
    if (socket) socket.emit('emote', { emote: emoteName });
    setShowEmotes(false);
  };

  // Players in the same area who are not in a fellowship (invite candidates)
  const nearbyInvitable = onlinePlayers
    .filter(p =>
      p.id !== character?.id &&
      p.current_area === character?.current_area
    )
    .sort((a, b) => {
      const aFriend = friends.includes(a.id) ? 0 : 1;
      const bFriend = friends.includes(b.id) ? 0 : 1;
      if (aFriend !== bFriend) return aFriend - bFriend;
      return (a.name || '').localeCompare(b.name || '');
    });

  // Filter the nearby list by search query (client-side since we already have the data)
  const filteredNearby = searchQuery.trim()
    ? nearbyInvitable.filter(p => p.name.toLowerCase().startsWith(searchQuery.toLowerCase()))
    : nearbyInvitable;

  // Server-side search for players in the same area (catches players not yet in onlinePlayers)
  const searchNearbyPlayers = useCallback(async (query) => {
    if (query.length < 1) { setSearchResults([]); setShowSearchResults(false); return; }
    try {
      const area = character?.current_area;
      const url = `${API}/api/social/search-players?q=${encodeURIComponent(query)}${area ? `&area=${encodeURIComponent(area)}` : ''}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (res.ok) {
        setSearchResults(data.players || []);
        setShowSearchResults((data.players || []).length > 0);
        setHighlightIdx(-1);
      }
    } catch (e) { /* ignore */ }
  }, [character?.current_area]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.trim().length >= 1) {
      searchTimer.current = setTimeout(() => searchNearbyPlayers(val.trim()), 250);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (!showSearchResults || searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      handleInvite(searchResults[highlightIdx].id);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchResults(false);
    } else if (e.key === 'Escape') {
      setShowSearchResults(false);
    }
  };

  if (!socket) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content fellowship-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🤝 Fellowship</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Emotes section — always available */}
        <div className="fellowship-section">
          <button
            className="btn-secondary fellowship-emote-toggle"
            onClick={() => setShowEmotes(!showEmotes)}
          >
            😀 Emotes {showEmotes ? '▲' : '▼'}
          </button>
          {showEmotes && (
            <div className="emote-grid">
              {EMOTES.map(e => (
                <button
                  key={e.name}
                  className="emote-btn"
                  onClick={() => handleEmote(e.name)}
                  title={e.label}
                >
                  <span className="emote-icon">{e.icon}</span>
                  <span className="emote-label">{e.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* No fellowship — create one */}
        {!inFellowship && (
          <div className="fellowship-section">
            <p className="fellowship-desc">
              Create a fellowship to explore and fight together! The leader controls movement, 
              and in combat everyone rolls initiative.
            </p>
            <button className="btn-primary" onClick={handleCreate}>🏳️ Create Fellowship</button>
          </div>
        )}

        {/* In a fellowship — show members */}
        {inFellowship && (
          <>
            <div className="fellowship-section">
              <h3>Members ({fellowship.members?.length || 0}/4)</h3>
              <div className="fellowship-members">
                {(fellowship.members || []).map(m => (
                  <div key={m.characterId} className={`fellowship-member ${m.characterId === fellowship.leaderId ? 'is-leader' : ''}`}>
                    <span className="fellowship-member-icon">
                      {m.characterId === fellowship.leaderId ? '👑' : '🛡️'}
                    </span>
                    <span className="fellowship-member-name">{m.characterName}</span>
                    {m.characterId !== character?.id && (
                      <div className="fellowship-member-actions">
                        {onInspect && (
                          <button className="btn-xs" onClick={() => onInspect(m.characterId)} title="Inspect">🔍</button>
                        )}
                        {isLeader && (
                          <button className="btn-xs" onClick={() => handleTransfer(m.characterId)} title="Make Leader">👑</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Invite nearby players */}
            {isLeader && fellowship.members.length < 4 && (
              <div className="fellowship-section">
                <h3>Invite Nearby Players</h3>
                <div className="autocomplete-wrapper" style={{ marginBottom: 10 }}>
                  <input
                    type="text"
                    placeholder="Search players nearby..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                    onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true); }}
                    autoComplete="off"
                    className="fellowship-search-input"
                  />
                  {showSearchResults && (
                    <div className="autocomplete-dropdown">
                      {searchResults.map((p, idx) => (
                        <div
                          key={p.id}
                          className={`autocomplete-item ${idx === highlightIdx ? 'highlighted' : ''} ${p.is_friend ? 'is-friend' : ''}`}
                          onMouseDown={() => {
                            handleInvite(p.id);
                            setSearchQuery('');
                            setSearchResults([]);
                            setShowSearchResults(false);
                          }}
                          onMouseEnter={() => setHighlightIdx(idx)}
                        >
                          <span className="autocomplete-name">
                            {p.is_friend && <span className="friend-badge" title="Friend">⭐</span>}
                            {p.name}
                          </span>
                          <span className="autocomplete-details">
                            Lv.{p.level} {p.advanced_class || p.class}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {filteredNearby.length === 0 ? (
                  <p className="fellowship-empty">
                    {searchQuery ? 'No matching players nearby.' : 'No players nearby to invite.'}
                  </p>
                ) : (
                  <div className="fellowship-invite-list">
                    {filteredNearby.map(p => (
                      <div key={p.id} className="fellowship-invite-row">
                        <span>
                          {friends.includes(p.id) && <span className="friend-badge" title="Friend">⭐</span>}
                          {p.name} (Lv.{p.level})
                        </span>
                        <button className="btn-sm btn-primary" onClick={() => handleInvite(p.id)}>Invite</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="fellowship-section fellowship-footer">
              {isLeader && (
                <p className="fellowship-leader-note">
                  ✨ You control the fellowship's movement. Everyone follows you!
                </p>
              )}
              {!isLeader && (
                <p className="fellowship-member-note">
                  {fellowship.leaderName} is leading the fellowship.
                </p>
              )}
              <button className="btn-danger btn-sm" onClick={handleLeave}>Leave Fellowship</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FellowshipPanel;
